import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Container,
    Grid,
    IconButton,
    Button,
    Divider,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    ArrowBack,
    CalendarToday,
    TrendingUp
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRequest } from "../../Fetch/request";
import Loader from '../Loader/Loader';

const PastSubmissions = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [submissions, setSubmissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const [selectedTag, setSelectedTag] = useState(() => {
        // Initialize with the tag from navigation state if available
        return location.state?.selectedTag || 'all';
    });
    const [availableTags, setAvailableTags] = useState([]);

    // Function to render formatted feedback with proper styling
    const renderFormattedFeedback = (content) => {
        if (typeof content !== 'string') {
            return <Typography variant="body2">{JSON.stringify(content, null, 2)}</Typography>;
        }

        // Split content by lines and process each line
        const lines = content.split('\n');
        const elements = [];
        
        lines.forEach((line, index) => {
            if (line.startsWith('## ')) {
                // Header
                const headerText = line.replace('## ', '').replace(/\*\*/g, '');
                elements.push(
                    <Typography 
                        key={index} 
                        variant="h6" 
                        sx={{ 
                            fontSize: '1rem',
                            fontWeight: 'bold', 
                            color: '#008080', 
                            mt: index > 0 ? 1.5 : 0, 
                            mb: 0.5 
                        }}
                    >
                        {headerText}
                    </Typography>
                );
            } else if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
                // Bold line
                const boldText = line.replace(/\*\*/g, '');
                elements.push(
                    <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ fontWeight: 'bold', color: '#333', mb: 0.3, fontSize: '0.9rem' }}
                    >
                        {boldText}
                    </Typography>
                );
            } else if (line.trim() === '') {
                // Empty line - add spacing
                elements.push(<Box key={index} sx={{ height: '6px' }} />);
            } else if (line.trim()) {
                // Regular text with inline bold formatting
                const parts = line.split(/(\*\*[^*]+\*\*)/);
                const formattedParts = parts.map((part, partIndex) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        return (
                            <Box 
                                key={partIndex} 
                                component="span" 
                                sx={{ fontWeight: 'bold', color: '#333' }}
                            >
                                {part.replace(/\*\*/g, '')}
                            </Box>
                        );
                    }
                    return part;
                });
                
                elements.push(
                    <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ 
                            lineHeight: 1.5, 
                            color: '#555', 
                            mb: 0.3,
                            fontSize: '0.9rem',
                            whiteSpace: 'pre-wrap'
                        }}
                    >
                        {formattedParts}
                    </Typography>
                );
            }
        });

        return <Box>{elements}</Box>;
    };



    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                // Set loading state based on whether it's initial load or filter change
                if (submissions.length === 0) {
                    setIsLoading(true);
                } else {
                    setIsFilterLoading(true);
                }
                
                const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
                
        
                
                // Build the API URL with optional tag filter
                let apiUrl = `${process.env.REACT_APP_URL}/core-questions/submissions?userId=${userId}`;
                
                // Add tag filter if a specific tag is selected (not 'all')
                if (selectedTag && selectedTag !== 'all') {
                    apiUrl += `&tag=${encodeURIComponent(selectedTag)}`;
                }
                

                
                const response = await getRequest(apiUrl);
                

                
                if (response && response.status === "200" && response.response) {
                    // Transform the API response to match our expected format
                    let transformedSubmissions = [];
                    
                    // Handle different response structures
                    if (Array.isArray(response.response)) {
                        transformedSubmissions = response.response.map((submission, index) => ({
                            id: submission.id || `submission_${index}`,
                            question: submission.question || "Question not available",
                            userAnswer: submission.answer || "",
                            aiAnswer: submission.aiAnswer || submission.ai_answer || "",
                            topic: submission.tag || submission.topic || "Unknown",
                            difficulty: submission.difficulty || "Medium",
                            submittedAt: submission.submittedAt || submission.created_at || new Date().toISOString(),
                            questionId: submission.questionId || submission.question_id || `q_${index}`
                        }));
                    } else if (response.response.submissions) {
                        transformedSubmissions = response.response.submissions.map((submission, index) => ({
                            id: submission.id || `submission_${index}`,
                            question: submission.question || "Question not available",
                            userAnswer: submission.answer || "",
                            aiAnswer: submission.aiAnswer || submission.ai_answer || "",
                            topic: submission.tag || submission.topic || "Unknown",
                            difficulty: submission.difficulty || "Medium",
                            submittedAt: submission.submittedAt || submission.created_at || new Date().toISOString(),
                            questionId: submission.questionId || submission.question_id || `q_${index}`
                        }));
                    }
                    
    
                    setSubmissions(transformedSubmissions);
                } else {

                    setSubmissions([]);
                }
            } catch (error) {
                setSubmissions([]);
            } finally {
                setIsLoading(false);
                setIsFilterLoading(false);
            }
        };

        fetchSubmissions();
    }, [selectedTag]); // Re-fetch when selectedTag changes

    // Update available tags from submissions whenever submissions change
    useEffect(() => {
        const topics = [...new Set(submissions.map(s => s.topic))];
        setAvailableTags(topics);
    }, [submissions]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty.toLowerCase()) {
            case 'easy': return '#4CAF50';
            case 'medium': return '#FF9800';
            case 'hard': return '#F44336';
            default: return '#666666';
        }
    };

    const filteredSubmissions = submissions.filter(submission => {
        const matchesTag = selectedTag === 'all' || submission.topic === selectedTag;
        return matchesTag;
    });

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px'
                }}>
                    <Loader />
                    <Typography variant="h5" sx={{ 
                        color: '#008080', 
                        mb: 2, 
                        mt: 3,
                        fontWeight: 'bold'
                    }}>
                        Loading Past Submissions...
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: '#666666',
                        maxWidth: 400
                    }}>
                        Please wait while we fetch your submission history
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                            onClick={() => navigate(-1)}
                            sx={{ mr: 2, color: '#008080' }}
                        >
                            <ArrowBack />
                        </IconButton>
                    </Box>
                    
                    {/* Tag Selector */}
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ color: '#008080' }}>Filter by Tag</InputLabel>
                        <Select
                            value={selectedTag}
                            label="Filter by Tag"
                            onChange={(e) => {
                                setSelectedTag(e.target.value);
                                // The useEffect will automatically re-fetch data when selectedTag changes
                            }}
                            disabled={isLoading}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#008080',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#008080',
                                },
                                '& .MuiSelect-icon': {
                                    color: '#008080',
                                },
                            }}
                        >
                            <MenuItem value="all">All Tags</MenuItem>
                            {['Operating Systems', 'Networking', 'DBMS', 'System Design', 'OOPs', 'Design Patterns'].map(tag => (
                                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Submissions List */}
            <Box>
                {isFilterLoading && (
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Loader />
                        <Typography variant="body1" sx={{ 
                            color: '#666666', 
                            mt: 2
                        }}>
                            Filtering submissions...
                        </Typography>
                    </Box>
                )}
                
                {!isFilterLoading && (
                    <>
                        {submissions.length === 0 ? (
                            <Paper sx={{ p: 6, textAlign: 'center' }}>
                                <TrendingUp sx={{ fontSize: 64, color: '#cccccc', mb: 3 }} />
                                <Typography variant="h5" sx={{ color: '#666666', mb: 2, fontWeight: 'bold' }}>
                                    Waiting for new questions to arrive
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#999999', mb: 4, maxWidth: 400, mx: 'auto' }}>
                                    No questions have been attempted yet. New questions will appear here once you start answering them.
                                </Typography>
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: '#008080',
                                        color: '#fff',
                                        padding: '12px 32px',
                                        borderRadius: '25px',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        '&:hover': {backgroundColor: '#006666'}
                                    }}
                                    onClick={() => navigate('/todays-question')}
                                >
                                    Start Answering Questions
                                </Button>
                            </Paper>
                        ) : filteredSubmissions.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ color: '#666666', mb: 1 }}>
                                    No submissions found
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#999999' }}>
                                    {selectedTag !== 'all'
                                        ? `No submissions found for "${selectedTag}". Try selecting a different tag or "All Tags".`
                                        : 'Waiting for new questions to arrive'
                                    }
                                </Typography>
                            </Paper>
                        ) : (
                            <Grid container spacing={3}>
                                {filteredSubmissions.map((submission) => (
                                    <Grid item xs={12} key={submission.id}>
                                        <Card sx={{
                                            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                            borderRadius: '12px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <CardContent>
                                                {/* Header */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <Chip 
                                                            label={submission.topic} 
                                                            size="small" 
                                                            sx={{ 
                                                                backgroundColor: '#008080', 
                                                                color: 'white',
                                                                fontWeight: 'bold'
                                                            }} 
                                                        />
                                                        <Chip 
                                                            label={submission.difficulty} 
                                                            size="small" 
                                                            sx={{ 
                                                                backgroundColor: getDifficultyColor(submission.difficulty), 
                                                                color: 'white',
                                                                fontWeight: 'bold'
                                                            }} 
                                                        />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CalendarToday sx={{ fontSize: 16, color: '#666666' }} />
                                                        <Typography variant="caption" sx={{ color: '#666666' }}>
                                                            {formatDate(submission.submittedAt)}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Question */}
                                                <Typography variant="h6" sx={{
                                                    fontWeight: '600',
                                                    color: '#333333',
                                                    mb: 2,
                                                    lineHeight: 1.4
                                                }}>
                                                    {submission.question}
                                                </Typography>

                                                <Divider sx={{ my: 2 }} />

                                                {/* User Answer */}
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{
                                                        fontWeight: '600',
                                                        color: '#008080',
                                                        mb: 1
                                                    }}>
                                                        Your Answer:
                                                    </Typography>
                                                    <Paper sx={{
                                                        p: 2,
                                                        backgroundColor: '#f9f9f9',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e0e0e0'
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            lineHeight: 1.6,
                                                            color: '#333333',
                                                            whiteSpace: 'pre-line'
                                                        }}>
                                                            {submission.userAnswer}
                                                        </Typography>
                                                    </Paper>
                                                </Box>

                                                {/* AI Answer */}
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="subtitle2" sx={{
                                                        fontWeight: '600',
                                                        color: '#4CAF50',
                                                        mb: 1
                                                    }}>
                                                        AI Feedback:
                                                    </Typography>
                                                    <Paper sx={{
                                                        p: 2,
                                                        backgroundColor: '#f0f8ff',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e0e0e0',
                                                        maxHeight: 300,
                                                        overflowY: 'auto'
                                                    }}>
                                                        <Box sx={{
                                                            lineHeight: 1.6,
                                                            color: '#333333',
                                                            '& h6': {
                                                                fontSize: '1rem',
                                                                fontWeight: 'bold',
                                                                color: '#008080',
                                                                marginBottom: '8px',
                                                                marginTop: '12px'
                                                            },
                                                            '& strong': {
                                                                fontWeight: 'bold',
                                                                color: '#333'
                                                            }
                                                        }}>
                                                            {renderFormattedFeedback(submission.aiAnswer || 'No AI feedback available for this submission.')}
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
};

export default PastSubmissions; 